<template>
    <aside
        :data-id="id"
        :class="[
            isVoiceCreator ? 'voice-badge--is-voiceCreator' : null,
            isShareToWin ? 'voice-badge--is-shareToWin' : null,
            isSelected ? 'voice-badge--is-selected' : null,
            isCustom ? 'voice-badge--is-custom' : null,
            isPro ? 'voice-badge--is-pro' : null,
            isDisabled ? 'voice-badge--is-disabled' : null,
            isNew ? 'voice-badge--is-new' : null,
            isLoading ? 'voice-badge--is-loading' : null,
            isSettingDetail ? 'voice-badge--is-setting-detail' : null,
            denyUserInteraction ? 'voice-badge--denyInteraction' : null,
            isFavourite ? 'voice-badge--is-favourite' : null,
            isEditable ? 'voice-badge--is-editable' : null,
            makeEditable ? 'voice-badge--is-current-edit' : null,
            hasIconChecksum ? 'voice-badge--has-checksum' : null,
            hasParentId ? 'voice-badge--has-parent-id' : null,
            hasEditImage ? 'voice-badge--has-edit-image' : null,
            'voice-badge'
        ]"
        :style="customStyle">
        <div class="voice-badge__wrap" :class="{'voice-badge__wrap--hovered': isMouseOver}">
            <!-- NEW badge label -->
            <span
                v-if="isNew"
                class="voice-badge__label-new">
            </span>
            <div class="voice-badge__filter">
                <!--
                    button to add/remove from favourite
                    Hide in voice creator case and share to win case
                -->
                <button
                    v-if="hasFavourite"
                    class="voice-badge__favourite"
                    :class="{
                        'voice-badge__favourite--always-visible': favouriteAlwaysVisible,
                        'voice-badge__favourite--selected': isSelected
                    }"
                    @click="setFavourite"
                >
                    <slot name="icon"/>
                </button>

                <!--
                    voice icon / create voice icon / share to win icon
                -->
                <button
                    class="voice-badge__icon"
                    :disabled="isDisabled"
                    @click="triggerAction"
                >
                </button>
            </div>
        </div>
        <div
            class="voice-badge__texts"
            @click="triggerAction"
            @mouseenter="isMouseOver = true"
            @mouseleave="isMouseOver = false"
        >
            <div class="voice-badge__label">
                <input
                    ref="name"
                    type="text"
                    :disabled="isNotEditable && !makeEditable"
                    :value="name"
                    maxlength="30"
                    spellcheck="false"
                    @keyup.enter="editEnterName"
                    @keyup.escape="editEscapeName"
                    @blur="makeEditable ? editEscapeName : null"
                />
                <!-- temp -->
                <BaseIcon
                    v-if="isEditable"
                    tag="button"
                    :name="!makeEditable ? 'edit' : 'check'"
                    size="small"
                    @onClick="editName"
                />
            </div>
            <button
                v-if="$slots['keybind'] && !isVoiceCreator && !isShareToWin"
                :class="[
                    hasKeyBinds ? 'voice-badge__add-key-bind--is-active' : null,
                    'voice-badge__add-key-bind'
                ]"
                @click="setKeyBind"
            >
                <slot name="keybind"></slot>
            </button>

            <div
                v-if="$slots['subtitle']"
                class="voice-badge__subtitle"
            >
                <slot name="subtitle"></slot>
            </div>
        </div>
    </aside>
</template>
<script>
    export default {
        name: 'voiceBadge',

        data() {
            return {
                makeEditable: false,
                isMouseOver: false
            }
        },

        components: {
            BaseIcon: () => import (/* webpackChunkName: "BaseIcon"*/ '@RepoComponent/BaseIcon/BaseIcon.vue'),
        },

        props: {
            id: {
                type: String
            },

            name: {
                type: String
            },

            isVoiceCreator: {
                type: Boolean,
                default: false
            },

            isShareToWin: {
                type: Boolean,
                default: false
            },

            isSelected: {
                type: Boolean,
                default: false
            },

            isCustom: {
                type: Boolean,
                default: false
            },

            isDisabled: {
                type: Boolean,
                default: false
            },

            isLoading: {
                type: Boolean,
                default: false
            },

            isPro: {
                type: Boolean,
                default: false
            },

            isNotEditable: {
                type: Boolean,
                default: true
            },

            isNew: {
                type: Boolean,
                default: false
            },

            isSettingDetail: {
                type: Boolean,
                default: false
            },

            hasFavourite: {
                type: Boolean,
                default: true
            },

            hasFilter: {
                type: String,
                default: 'melt'
            },

            hasActionDisabled: {
                type: Boolean,
                default: false
            },

            denyUserInteraction: {
                type: Boolean,
                default: false
            },

            icon: {
                type: String,
            },

            isFavourite: {
                type: Boolean,
                default: true
            },

            hasParentId: {
                type: String
            },

            rating: {
                type: Number,
                default: 0
            },

            hasKeyBinds: {
                type: Boolean,
                default: false
            },

            keybind: {
                type: Array,
                default: () => []
            },

            isEditable: {
                type: Boolean,
                default: false
            },

            customImage: {
                type: String
            },

            hasIconChecksum: {
                type: String,
                default: ''
            },

            hasSavedImage: {
                type: String
            },

            hasEditImage: {
                type: String,
            },

            favouriteAlwaysVisible: {
                type: Boolean,
                default: false,
            },

            urlBackground: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            customStyle() {
                return {
                    '--custom-face-image' : this.iconSRC,
                    '--custom-edit-image' : this.voiceIconBackground,
                }
            },

            iconSRC() {
                return this.customImage || this.urlBackground
                    ? `url(${this.icon})`
                    : this.icon.includes('fcorevoice') || this.icon.includes('fvlabvoice')
                        ? `url(${this.icon})`
                        : `url(${require('@/assets/images/voices/' + this.icon + '')})`;
            },

            voiceIconBackground() {
                const path = this.hasEditImage ? this.hasEditImage : 'voice-custom-1.png';
                return `url(${require('@/assets/images/voices/' + path)})`;
            }
        },

        methods: {
            setAction() {
                this.$emit('userClick', {
                    name: this.name,
                    id: this.id,
                    custom: this.isCustom,
                    active: !this.isPro,
                    parent_voice_id: this.hasParentId,
                    rating: this.rating,
                    keybind: this.keybind,
                    icon_checksum: this.hasIconChecksum,
                    icon: this.hasSavedImage,
                    customImage: null
                })
            },

            setFavourite() {
                this.$emit('userFavourite', {
                    id: this.id
                })
            },

            setKeyBind() {
                this.$emit('userClick', {
                    name: this.name,
                    id: this.id,
                    custom: this.isCustom,
                    active: !this.isPro,
                    parent_voice_id: this.hasParentId,
                    rating: this.rating,
                    keyBindActive: true,
                    keybind: this.keybind,
                    icon_checksum: this.hasIconChecksum,
                    icon: this.hasSavedImage,
                    customImage: null
                })
            },

            editName() {
                if(this.makeEditable) {
                    this.$emit('editName', this.$refs.name.value)
                } else {
                    setTimeout(() => {
                        this.$emit('editEscape', true)
                        this.$refs.name.focus()
                        this.$refs.name.select()
                    }, 200);

                }

                this.makeEditable =! this.makeEditable
            },

            editEnterName() {
                this.makeEditable = false
                this.$emit('editName', this.$refs.name.value)
            },

            editEscapeName() {
                this.makeEditable = false
                this.$emit('editEscape', false)
            },

            triggerAction() {
                !this.isSettingDetail
                    ? this.setAction()
                    : !this.isCustom || this.id.includes('duplicate') ? null : this.setAction()
            }
        },

    }
</script>

<style lang="scss" scoped src="./VoiceBadge.scss"></style>
